import { AnimatedPillSegment } from '@affine/core/components/animation/animated-pill-segment';
import { useI18n } from '@affine/i18n';
import { CloseIcon, ExpandFullIcon, SearchIcon } from '@blocksuite/icons/rc';
import * as Dialog from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';

import * as styles from './search-mode-header.css';
import type { SearchSurfaceMode } from './search-surface-state';

/** The fixed top of the search frame: quick/advanced switch, status, close. */
export function SearchModeHeader({
  mode,
  onModeChange,
  status,
}: {
  mode: SearchSurfaceMode;
  onModeChange: (mode: SearchSurfaceMode) => void;
  /** Right-aligned context of the active view, e.g. the indexing progress. */
  status?: ReactNode;
}) {
  const i18n = useI18n();
  return (
    <>
      <AnimatedPillSegment<SearchSurfaceMode>
        active={mode}
        ariaLabel={i18n.t({ i18nKey: 'com.streamlain.quicksearch.mode.label' })}
        onChange={onModeChange}
        options={[
          {
            key: 'quick',
            testId: 'search-mode-quick',
            label: (
              <span className={styles.option}>
                <SearchIcon />
                {i18n.t({ i18nKey: 'com.streamlain.quicksearch.mode.quick' })}
              </span>
            ),
          },
          {
            key: 'advanced',
            testId: 'search-mode-advanced',
            label: (
              <span className={styles.option}>
                <ExpandFullIcon />
                {i18n.t({
                  i18nKey: 'com.streamlain.quicksearch.mode.advanced',
                })}
              </span>
            ),
          },
        ]}
      />
      <div className={styles.status}>{status}</div>
      <Dialog.Close
        className={styles.close}
        aria-label={i18n.t({
          i18nKey: 'com.streamlain.quicksearch.close',
        })}
        data-testid="search-close"
      >
        <CloseIcon />
      </Dialog.Close>
    </>
  );
}
